import { z } from "zod";

/** API error catalog (specs operator-auth, risk-verification). Codes are stable and never translated. */
export const API_ERRORS = {
  invalid_request: { status: 400, description: "Request fields are missing or invalid." },
  invalid_client: {
    status: 401,
    description: "Unknown, revoked or malformed credentials.",
  },
  environment_mismatch: {
    status: 401,
    description: "The credential belongs to the other operator environment's installation.",
  },
  missing_token: {
    status: 401,
    description: "The Authorization: Bearer session token is missing.",
  },
  invalid_token: {
    status: 401,
    description: "Bad signature, wrong issuer or audience, or a token of another installation.",
  },
  token_expired: { status: 401, description: "The session token has expired; obtain a new one." },
  token_revoked: { status: 401, description: "The session token or its credential was revoked." },
  product_not_enabled: {
    status: 403,
    description: "The product is not enabled for the operator environment.",
  },
  product_mismatch: {
    status: 403,
    description: "The session token was issued for another product.",
  },
  origin_not_allowed: {
    status: 403,
    description: "The request origin is not registered for this operator environment.",
  },
  not_found: { status: 404, description: "The resource does not exist for this operator." },
  replay_detected: {
    status: 409,
    description: "The nonce was already used with this session token.",
  },
  credential_limit_reached: {
    status: 409,
    description: "The operator already has 2 active credentials in this installation.",
  },
  stale_signals: {
    status: 422,
    description: "Signals collected more than 2 min ago or too far in the future.",
  },
  invalid_signals: { status: 422, description: "Signals do not match the product mode's schema." },
  rate_limited: {
    status: 429,
    description: "Too many requests; retry after the Retry-After seconds.",
  },
  server_error: {
    status: 503,
    description:
      "A dependency needed to decide safely is unavailable, or the service is busy (overload, with Retry-After: retry shortly with jitter); no decision was made.",
  },
} as const satisfies Record<string, { status: number; description: string }>;

export type ApiErrorCode = keyof typeof API_ERRORS;
export const API_ERROR_CODES = Object.keys(API_ERRORS) as ApiErrorCode[];

export const ApiErrorCodeSchema = z
  .enum(API_ERROR_CODES as [ApiErrorCode, ...ApiErrorCode[]])
  .meta({ id: "ApiErrorCode" });

export const ApiError = z
  .object({
    error: ApiErrorCodeSchema,
    message: z.string(),
    /** Invalid fields for invalid_request/invalid_signals, or a reason such as player_ref_looks_like_pii. */
    details: z
      .object({
        fields: z.array(z.string()).optional(),
        reason: z.string().optional(),
      })
      .optional(),
  })
  .meta({ id: "ApiError" });
export type ApiError = z.infer<typeof ApiError>;

export function apiError(error: ApiErrorCode, details?: ApiError["details"]): ApiError {
  return { error, message: API_ERRORS[error].description, ...(details ? { details } : {}) };
}

/**
 * Console login refusals of the Explorer API (spec explorer-access, ADR 0006). A separate catalog:
 * these codes are never answered by the Token or Verify APIs.
 */
export const CONSOLE_AUTH_ERRORS = {
  invalid_credentials: {
    status: 401,
    description: "Email or password is wrong (the same answer for an unknown email).",
  },
  account_locked: {
    status: 423,
    description: "Too many failed logins; the account is locked for the remaining seconds.",
  },
  sso_required: {
    status: 403,
    description: "The organization signs in through its identity provider (SSO).",
  },
  invalid_mfa_code: {
    status: 401,
    description:
      "The authenticator code is wrong or was already used; it counts as a failed login.",
  },
  mfa_challenge_expired: {
    status: 401,
    description:
      "The code step (3 minutes) or the set-up step (10 minutes) expired, 3 wrong codes were entered, or it belongs to another browser; enter the password again.",
  },
  mfa_reset_pending: {
    status: 403,
    description:
      "The two-factor authentication was reset; use the recovery invitation from an Administrator.",
  },
  invalid_reset_code: {
    status: 400,
    description:
      "The password reset code is wrong, expired, already used or replaced (the same answer for an email that got no code); ask for a new code.",
  },
} as const satisfies Record<string, { status: number; description: string }>;

export type ConsoleAuthErrorCode = keyof typeof CONSOLE_AUTH_ERRORS;
export const CONSOLE_AUTH_ERROR_CODES = Object.keys(CONSOLE_AUTH_ERRORS) as ConsoleAuthErrorCode[];

export const ConsoleAuthError = z.discriminatedUnion("error", [
  z.object({ error: z.literal("invalid_credentials"), message: z.string() }).strict(),
  z
    .object({
      error: z.literal("account_locked"),
      message: z.string(),
      details: z.object({ locked_for_seconds: z.int().positive() }).strict(),
    })
    .strict(),
  z.object({ error: z.literal("sso_required"), message: z.string() }).strict(),
  z.object({ error: z.literal("invalid_mfa_code"), message: z.string() }).strict(),
  z.object({ error: z.literal("mfa_challenge_expired"), message: z.string() }).strict(),
  z.object({ error: z.literal("mfa_reset_pending"), message: z.string() }).strict(),
  z.object({ error: z.literal("invalid_reset_code"), message: z.string() }).strict(),
]);
export type ConsoleAuthError = z.infer<typeof ConsoleAuthError>;
